import 'intersection-observer';

import React from 'react';
import { hydrate } from 'react-dom';

import { init as SentryInit } from '@sentry/browser';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { createPersistedQueryLink } from 'apollo-link-persisted-queries';
import { RetryLink } from 'apollo-link-retry';

import introspectionQueryResultData from '../../types/__generated_fragments__.json';
import AppWithProviders from '../components/AppWithProviders/AppWithProviders';
import clientConfig from './config';
import i18n from './i18nClient';
import { createAuth } from '../utils/authCreator';
import { fetchWithTokenRefresh } from './fetchWithTokenRefresh';
import { manageAnalyticsCookie } from './manageAnalyticsCookie';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const auth = createAuth(document);

manageAnalyticsCookie(auth);

const link = ApolloLink.from([
  createPersistedQueryLink({ useGETForHashedQueries: true }),
  new RetryLink({
    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: error => !!error,
    },
  }),
  createHttpLink({
    uri: clientConfig.graphqlUrl,
    fetch: (input, init) => {
      if (window.__PageMetaContext.isPreview) {
        init = { ...init, credentials: 'include' };
      }

      if (init?.credentials === 'include') {
        return fetchWithTokenRefresh(auth, input, init);
      }

      return fetch(input, init);
    },
  }),
]);

const client = new ApolloClient({
  name: `JG.Pages.UI (client side, ${window.__PageMetaContext.pageType})`,
  version: window.__RELEASE_VERSION,
  link,
  cache: new InMemoryCache({ fragmentMatcher, freezeResults: true }).restore(
    window.__APOLLO_STATE__,
  ),
  assumeImmutableResults: true,
});

if (
  clientConfig.sentry &&
  clientConfig.sentry.dsn &&
  clientConfig.sentry.config
) {
  /* eslint-disable no-underscore-dangle */
  const revision = window.__RELEASE_VERSION;
  const release = revision && revision.toString();

  SentryInit({
    dsn: clientConfig.sentry.dsn,
    release,
    environment: clientConfig.sentry.config.environment,
    whitelistUrls: [/justgiving\.com/, /jg-cdn\.com/],
    sampleRate: 0.1,
    ignoreErrors: [
      /Circuit open for request/,
      /Request timeout after/,
      /Network error: Failed to fetch/,
    ],
  });
}

hydrate(
  <AppWithProviders
    pageMetaContextProviderValue={window.__PageMetaContext}
    apolloProviderClient={client}
    i18n={i18n}
    auth={auth}
  />,
  document.getElementById('content')!,
);
